// src/components/forms/SimpleToolbar.tsx
import React from 'react';
import { 
  useRedirect, 
  useTranslate,
  Toolbar,
  SaveButton,
  DeleteButton,
  ToolbarProps, 
} from 'react-admin';
import { 
  Box, 
  Button, 
  useTheme, 
  useMediaQuery,
} from '@mui/material';
import { 
  Close,
  Save as SaveIcon,
} from '@mui/icons-material';

interface MyToolbarProps extends ToolbarProps {
  onCancel?: () => void;
  resource?: string;
  showDelete?: boolean;
  onDelete?: () => void;
  deleteButton?: boolean;
}

export const MyToolbar: React.FC<MyToolbarProps> = ({
  onCancel,
  resource = '',
  showDelete = false,
  onDelete,
  deleteButton = false,
  ...rest // Capturar el resto de props de react-admin
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const translate = useTranslate();
  const redirect = useRedirect();
 
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      redirect('list', resource);
    }
  };

  return (
    <Toolbar
      {...rest} 
      sx={{
        marginTop: 'auto',
        position: 'sticky',
        bottom: 0, 
        
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 2 : 3,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        zIndex: 1100,
        ...(isMobile && {
          position: 'fixed',
          boxShadow: `0 -4px 12px ${theme.palette.action.hover}`,
        }),
      }}
    >
      {/* Botones de acción a la izquierda */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        
        {/* Botón Eliminar condicional */}
        {(showDelete || deleteButton) && (
          <>
            {onDelete ? (
              // Si se proporciona un handler personalizado, usar un botón normal
              <Button
                variant="outlined"
                color="error"
                onClick={onDelete}
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: isMobile ? 80 : 100,
                  ...(isMobile && {
                    fontSize: '0.875rem',
                  }),
                }}
              >
                {translate('ra.action.delete', 'Eliminar')}
              </Button>
            ) : (
              // Si no hay handler, usar el DeleteButton de React Admin
              <DeleteButton
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  ...(isMobile && {
                    fontSize: '0.875rem',
                  }),
                }}
              />
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {/* Botón Cancelar */}
        <Button
          variant={isMobile ? 'text' : 'outlined'}
          startIcon={!isMobile && <Close />}
          onClick={handleCancel}
          size={isMobile ? 'small' : 'medium'}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            minWidth: isMobile ? 80 : 100,
            ...(isMobile && {
              fontSize: '0.875rem',
            }),
          }}
        >
          {translate('ra.action.cancel', 'Cancelar')}
        </Button>

        {/* Botón Guardar a la derecha */}
        <SaveButton
          variant="contained"
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          icon={<SaveIcon />}
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            minWidth: isMobile ? 80 : 120,
            ...(isMobile && {
              fontSize: '0.875rem',
            }),
          }}
        />
      </Box>
    </Toolbar>
  );
};

export default MyToolbar;